import { createMMKV } from 'react-native-mmkv';

// ─────────────────────────────────────────────────────────────────────────────

export function setSharedLineItemAttribution(params: SetSharedLineItemAttributionParams) {
  const { sharedLineItemProductName, sharedLineItemSlug } = params;

  const attribution = getSharedLineItemAttribution();

  const entries = attribution.entries.filter(
    (entry) => entry.sharedLineItemSlug !== sharedLineItemSlug,
  );
  const existingEntry = attribution.entries.find(
    (entry) => entry.sharedLineItemSlug === sharedLineItemSlug,
  );

  const cartLineItemIds = existingEntry?.cartLineItemIds ?? [];

  setAttributionState({
    entries: [...entries, { sharedLineItemProductName, sharedLineItemSlug, cartLineItemIds }],
  });
}

export function addCartLineItemToSharedLineItemAttribution(
  params: AddCartLineItemToSharedLineItemAttributionParams,
) {
  const { sharedLineItemProductName, sharedLineItemSlug, cartLineItemId } = params;

  const attribution = getSharedLineItemAttribution();

  const existingEntry = attribution.entries.find(
    (entry) => entry.sharedLineItemSlug === sharedLineItemSlug,
  );
  const remainingEntries = attribution.entries.filter(
    (entry) => entry.sharedLineItemSlug !== sharedLineItemSlug,
  );

  const cartLineItemIds = Array.from(
    new Set([...(existingEntry?.cartLineItemIds ?? []), cartLineItemId]),
  );

  setAttributionState({
    entries: [
      ...remainingEntries,
      {
        sharedLineItemProductName,
        sharedLineItemSlug,
        cartLineItemIds,
      },
    ],
  });
}

export function clearSharedLineItemAttribution() {
  SHARED_LINE_ITEM_ATTRIBUTION_STORAGE.remove(ATTRIBUTION_STORAGE_KEY);
}

export function getSharedLineItemAttribution() {
  const storedAttribution = SHARED_LINE_ITEM_ATTRIBUTION_STORAGE.getString(ATTRIBUTION_STORAGE_KEY);

  if (!storedAttribution) {
    return EMPTY_ATTRIBUTION;
  }

  try {
    return parseAttributionState(JSON.parse(storedAttribution));
  } catch {
    return EMPTY_ATTRIBUTION;
  }
}

export function getSharedLineItemProductName(params: SharedLineItemProductNameParams) {
  const { sharedLineItemSlug, attribution = getSharedLineItemAttribution() } = params;

  if (!sharedLineItemSlug) return;

  return attribution.entries.find((entry) => entry.sharedLineItemSlug === sharedLineItemSlug)
    ?.sharedLineItemProductName;
}

export function getSharedLineItemCartLineItemTelemetry(params: CartLineItemTelemetryParams) {
  const { cartLineItemId, attribution = getSharedLineItemAttribution() } = params;

  if (!cartLineItemId) return { isSharedLineItem: false };

  const attributionEntry = attribution.entries.find((entry) =>
    entry.cartLineItemIds.includes(cartLineItemId),
  );

  const sharedLineItemSlug = attributionEntry?.sharedLineItemSlug;
  const sharedLineItemProductName = attributionEntry?.sharedLineItemProductName;

  return {
    lineItemId: cartLineItemId,
    isSharedLineItem: Boolean(attributionEntry),
    ...(sharedLineItemSlug ? { sharedLineItemSlug } : {}),
    ...(sharedLineItemProductName ? { sharedLineItemProductName } : {}),
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function setAttributionState(attribution: SharedLineItemAttributionState) {
  SHARED_LINE_ITEM_ATTRIBUTION_STORAGE.set(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
}

function parseAttributionState(value: unknown): SharedLineItemAttributionState {
  if (!checkIfRecord(value)) {
    return EMPTY_ATTRIBUTION;
  }

  const entries = Array.isArray(value.entries)
    ? value.entries.map(parseAttributionEntry).filter(checkIfAttributionEntry)
    : [];

  return { entries };
}

function parseAttributionEntry(value: unknown): SharedLineItemAttributionEntry | undefined {
  if (!checkIfRecord(value)) {
    return;
  }

  const cartLineItemIds = Array.isArray(value.cartLineItemIds)
    ? value.cartLineItemIds.filter(checkIfString)
    : [];

  if (!checkIfString(value.sharedLineItemSlug)) {
    return;
  }

  return {
    cartLineItemIds,
    sharedLineItemProductName: checkIfString(value.sharedLineItemProductName)
      ? value.sharedLineItemProductName
      : undefined,
    sharedLineItemSlug: value.sharedLineItemSlug,
  };
}

function checkIfRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function checkIfString(value: unknown): value is string {
  return typeof value === 'string';
}

function checkIfAttributionEntry(
  value: SharedLineItemAttributionEntry | undefined,
): value is SharedLineItemAttributionEntry {
  return Boolean(value);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SHARED_LINE_ITEM_ATTRIBUTION_STORAGE = createMMKV({ id: 'sg.shared-line-item-attribution' });
const ATTRIBUTION_STORAGE_KEY = 'shared-line-item-attribution';
const EMPTY_ATTRIBUTION: SharedLineItemAttributionState = { entries: [] };

// ─── Types ───────────────────────────────────────────────────────────────────

type SetSharedLineItemAttributionParams = {
  sharedLineItemSlug: string;
  sharedLineItemProductName: string;
};

type AddCartLineItemToSharedLineItemAttributionParams = {
  sharedLineItemSlug: string;
  sharedLineItemProductName: string | undefined;
  cartLineItemId: string;
};

type CartLineItemTelemetryParams = {
  cartLineItemId?: string;
  attribution?: SharedLineItemAttributionState;
};

type SharedLineItemProductNameParams = {
  sharedLineItemSlug?: string;
  attribution?: SharedLineItemAttributionState;
};

type SharedLineItemAttributionState = {
  entries: SharedLineItemAttributionEntry[];
};

type SharedLineItemAttributionEntry = {
  sharedLineItemSlug: string;
  sharedLineItemProductName: string | undefined;
  cartLineItemIds: string[];
};
